import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';

/**
 * Searchable / combobox select.
 *
 * Props:
 *   id          – forwarded to the inner <input> (for <label htmlFor>)
 *   options     – [{ value, label }]
 *   value       – controlled string value
 *   onChange    – (value: string) => void
 *   placeholder – shown when empty
 *   disabled
 *   hasError    – applies red-border error styling
 *   allowCustom – (default true) shows "Use '…' as custom type" when no exact match
 */
const SearchableSelect = ({
  id,
  options = [],
  value = '',
  onChange,
  placeholder = 'Select or type…',
  disabled = false,
  hasError = false,
  allowCustom = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Return label for a value; for custom (unknown) values, return the raw string.
  const getLabel = useCallback(
    (val) => {
      if (!val) return '';
      const opt = options.find((o) => o.value === val);
      return opt ? opt.label : val;
    },
    [options]
  );

  // Filtered options based on current search text
  const filtered = options.filter(
    (o) =>
      o.label.toLowerCase().includes(inputText.toLowerCase()) ||
      o.value.toLowerCase().includes(inputText.toLowerCase())
  );

  const exactMatch = options.some(
    (o) => o.label.toLowerCase() === inputText.trim().toLowerCase()
  );
  const showCustom = allowCustom && inputText.trim() !== '' && !exactMatch;

  // Full list for keyboard index
  const allItems = showCustom ? [...filtered, { __custom: true }] : filtered;

  // ── Position ─────────────────────────────────────────────────────────────

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, []);

  // ── Open / Close ─────────────────────────────────────────────────────────

  const openDropdown = useCallback(() => {
    if (disabled) return;
    updatePosition();
    setInputText('');
    setHighlighted(-1);
    setIsOpen(true);
  }, [disabled, updatePosition]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setInputText('');
    setHighlighted(-1);
  }, []);

  // ── Select handlers ───────────────────────────────────────────────────────

  const selectOption = useCallback(
    (optValue) => {
      onChange(optValue);
      closeDropdown();
    },
    [onChange, closeDropdown]
  );

  const selectCustom = useCallback(() => {
    const customValue = inputText.trim().toLowerCase().replace(/\s+/g, '_');
    if (customValue) onChange(customValue);
    closeDropdown();
  }, [inputText, onChange, closeDropdown]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Keep dropdown aligned on scroll / resize
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inContainer && !inDropdown) closeDropdown();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, closeDropdown]);

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleFocus = () => {
    if (!isOpen) openDropdown();
  };

  const handleBlur = (e) => {
    // Keep open when focus moves to dropdown portal (relatedTarget = null when
    // mousedown was prevented, so we only close for real focus transfers like Tab)
    const { relatedTarget } = e;
    if (!relatedTarget) return; // mousedown-prevented click → stay open
    if (
      containerRef.current?.contains(relatedTarget) ||
      dropdownRef.current?.contains(relatedTarget)
    )
      return;
    closeDropdown();
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    setHighlighted(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    closeDropdown();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDropdown();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        closeDropdown();
        break;
      case 'Tab':
        closeDropdown();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlighted((h) => Math.min(h + 1, allItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlighted((h) => Math.max(h - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlighted >= 0 && highlighted < allItems.length) {
          const item = allItems[highlighted];
          if (item.__custom) selectCustom();
          else selectOption(item.value);
        } else {
          closeDropdown();
        }
        break;
      default:
        break;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const inputValue = isOpen ? inputText : getLabel(value);

  const dropdownContent = (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-surface-raised border border-surface-border rounded-md shadow-glow-md overflow-y-auto max-h-48 animate-appear"
    >
      {filtered.length === 0 && !showCustom && (
        <div className="px-3 py-2 text-sm text-text-muted">No options found</div>
      )}

      {filtered.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => selectOption(opt.value)}
          className={[
            'w-full text-left px-3 py-2 text-sm transition-colors',
            highlighted === i
              ? 'bg-surface-overlay text-text-primary'
              : 'text-text-secondary hover:bg-surface-overlay hover:text-text-primary',
            opt.value === value ? 'font-medium !text-accent' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {opt.label}
        </button>
      ))}

      {showCustom && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={selectCustom}
          className={[
            'w-full text-left px-3 py-2 text-sm transition-colors border-t border-surface-border',
            highlighted === filtered.length
              ? 'bg-surface-overlay text-accent'
              : 'text-accent/80 hover:bg-surface-overlay hover:text-accent',
          ].join(' ')}
        >
          Use &ldquo;{inputText.trim()}&rdquo; as custom type
        </button>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        readOnly={!isOpen}
        onChange={isOpen ? handleInputChange : undefined}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        className={[
          'input-base pr-8',
          isOpen ? 'cursor-text' : 'cursor-pointer',
          hasError ? 'border-red-500 focus:ring-red-500' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      />

      {/* Right icon: clear button when a value is set, chevron otherwise */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
        {value && !disabled ? (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            className="text-text-muted hover:text-text-secondary transition-colors p-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ChevronDown
            className={`w-4 h-4 text-text-muted transition-transform duration-200 pointer-events-none ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>

      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default SearchableSelect;
